import React, { useRef, useState } from "react";
import { Box, Dialog, IconButton, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import PlayArrowRounded from "@mui/icons-material/PlayArrowRounded";
import ReplayRounded from "@mui/icons-material/ReplayRounded";
import VolumeOffRounded from "@mui/icons-material/VolumeOffRounded";
import VolumeUpRounded from "@mui/icons-material/VolumeUpRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import { useTranslatedDictionary } from "./hooks/useTranslatedDictionary";

const POST_MEDIA_TEXT = {
  es: {
    postImageAlt: "Publicación",
    mediaItems: "elementos multimedia",
  },
  en: {
    postImageAlt: "Post",
    mediaItems: "media items",
  },
};

const normalizeMedia = (post) => {
  if (!post) return [];
  if (Array.isArray(post.media) && post.media.length > 0) return post.media;

  const items = [];
  if (post.imageUrl) items.push({ type: "image", url: post.imageUrl });
  if (post.videoUrl) items.push({ type: "video", url: post.videoUrl });
  return items;
};

const MIN_ASPECT_RATIO = 9 / 16; // tallest allowed (portrait)
const MAX_ASPECT_RATIO = 16 / 9; // widest allowed (landscape)

const VideoPlayer = ({ url, autoplayVideos, isMulti, maxHeight }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(autoplayVideos);
  const [muted, setMuted] = useState(true);
  const [ended, setEnded] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(MIN_ASPECT_RATIO);
  const [expanded, setExpanded] = useState(false);

  const handleLoadedMetadata = () => {
    const video = videoRef.current;
    if (!video?.videoWidth || !video?.videoHeight) return;
    const ratio = video.videoWidth / video.videoHeight;
    setAspectRatio(Math.min(MAX_ASPECT_RATIO, Math.max(MIN_ASPECT_RATIO, ratio)));
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  };

  const restart = () => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = 0;
    setEnded(false);
    video.play().catch(() => {});
  };

  const closeExpanded = (e) => {
    e?.stopPropagation();
    setExpanded(false);
    videoRef.current?.pause();
  };

  const handleContainerClick = () => {
    if (ended) {
      restart();
      return;
    }
    if (expanded) {
      togglePlay();
      return;
    }
    setExpanded(true);
    requestAnimationFrame(() => videoRef.current?.play().catch(() => {}));
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setMuted(video.muted);
  };

  const isPortrait = aspectRatio <= 0.8;
  const singleMaxHeight = isPortrait ? "70vh" : maxHeight;
  const expandedFullscreen = expanded && isMobile;

  const player = (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        width: "100%",
        height: expandedFullscreen ? "100%" : isMulti ? 240 : "auto",
        maxHeight: expandedFullscreen ? "none" : expanded ? "85vh" : isMulti ? 240 : singleMaxHeight,
        aspectRatio: expanded ? undefined : isMulti ? undefined : aspectRatio,
        bgcolor: "#000",
        cursor: "pointer",
      }}
      onClick={handleContainerClick}
    >
      <Box
        component="video"
        ref={videoRef}
        src={url}
        controls={false}
        autoPlay={autoplayVideos}
        muted={muted}
        playsInline
        onLoadedMetadata={handleLoadedMetadata}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setEnded(true);
        }}
        sx={{
          width: "100%",
          height: "100%",
          objectFit: expanded ? "contain" : isMulti ? "cover" : "contain",
          display: "block",
        }}
      />

      {!playing && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <Box
            sx={{
              bgcolor: "rgba(0,0,0,0.5)",
              borderRadius: "50%",
              width: 56,
              height: 56,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {ended ? (
              <ReplayRounded sx={{ color: "#fff", fontSize: 30 }} />
            ) : (
              <PlayArrowRounded sx={{ color: "#fff", fontSize: 32 }} />
            )}
          </Box>
        </Box>
      )}

      <IconButton
        onClick={toggleMute}
        size="small"
        sx={{
          position: "absolute",
          right: 8,
          bottom: 8,
          bgcolor: "rgba(0,0,0,0.5)",
          color: "#fff",
          "&:hover": { bgcolor: "rgba(0,0,0,0.65)" },
        }}
      >
        {muted ? <VolumeOffRounded fontSize="small" /> : <VolumeUpRounded fontSize="small" />}
      </IconButton>

      {expanded && (
        <IconButton
          onClick={closeExpanded}
          size="small"
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            bgcolor: "rgba(0,0,0,0.5)",
            color: "#fff",
            "&:hover": { bgcolor: "rgba(0,0,0,0.65)" },
          }}
        >
          <CloseRounded fontSize="small" />
        </IconButton>
      )}
    </Box>
  );

  if (expanded) {
    return (
      <Dialog
        open
        onClose={closeExpanded}
        fullScreen={isMobile}
        maxWidth={isMobile ? false : "sm"}
        fullWidth={!isMobile}
        PaperProps={{
          sx: {
            bgcolor: "#000",
            boxShadow: "none",
            overflow: "hidden",
            ...(isMobile
              ? {
                  m: 0,
                  borderRadius: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }
              : {}),
          },
        }}
      >
        {player}
      </Dialog>
    );
  }

  return player;
};

const PostMedia = ({ post, maxHeight = 320, autoplayVideos = false, isLightTheme = false, language = "es" }) => {
  const t = useTranslatedDictionary(POST_MEDIA_TEXT, language);
  const items = normalizeMedia(post);
  if (items.length === 0) return null;

  const isMulti = items.length > 1;
  const onSurface = (opacity = 1) =>
    isLightTheme ? `rgba(15,20,32,${opacity})` : `rgba(255,255,255,${opacity})`;

  return (
    <Stack
      spacing={1}
      sx={{
        mt: 1.5,
        mb: 1.5,
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: isMulti ? "repeat(2, minmax(0, 1fr))" : "1fr",
          gap: 1,
        }}
      >
        {items.map((item, index) => {
          const isVideo = item?.type === "video" || (item?.url && /\.(mp4|webm|ogg)(\?|$)/i.test(item.url));
          return (
            <Box
              key={`${item?.url || index}`}
              sx={{
                borderRadius: "14px",
                overflow: "hidden",
                bgcolor: onSurface(0.04),
                border: `1px solid ${onSurface(0.08)}`,
                maxHeight: isMulti ? 240 : isVideo ? undefined : maxHeight,
              }}
            >
              {isVideo ? (
                <VideoPlayer url={item.url} autoplayVideos={autoplayVideos} isMulti={isMulti} maxHeight={maxHeight} />
              ) : (
                <Box
                  component="img"
                  src={item.url}
                  alt={t("postImageAlt")}
                  sx={{
                    width: "100%",
                    height: isMulti ? 240 : "auto",
                    maxHeight: isMulti ? 240 : maxHeight,
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>

      {isMulti && (
        <Typography variant="caption" sx={{ color: onSurface(0.55) }}>
          {items.length} {t("mediaItems")}
        </Typography>
      )}
    </Stack>
  );
};

export default PostMedia;
